import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  FileText, AirplaneTilt, Anchor, ArrowRight, CheckCircle, Clock
} from '@phosphor-icons/react';

const API = '/api';

export default function Reports() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [investigations, setInvestigations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/investigations`, { withCredentials: true });
        setInvestigations(res.data || []);
      } catch (e) {
        console.error('Failed to load reports:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-8 text-slate-500">Loading reports...</div>;

  return (
    <div className="max-w-5xl" data-testid="reports-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Reports</h1>
        <p className="text-sm text-slate-500 mt-1">ICAO Annex 13 investigation reports</p>
      </div>

      {investigations.length === 0 ? (
        <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
          <FileText size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No investigations found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {investigations.map((inv) => (
            <div
              key={inv.id}
              className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between hover:border-slate-300 transition-colors"
              data-testid={`report-row-${inv.id}`}
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${inv.sector === 'aviation' ? 'bg-blue-50' : 'bg-cyan-50'}`}>
                  {inv.sector === 'aviation' ? <AirplaneTilt size={18} className="text-blue-600" /> : <Anchor size={18} className="text-cyan-600" />}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-slate-800">{inv.investigation_number}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      inv.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      inv.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {inv.status || 'initiated'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{inv.title}</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="rounded-lg shrink-0"
                onClick={() => navigate(`/investigations/${inv.id}/report`)}
                data-testid={`open-report-${inv.id}`}
              >
                <FileText size={14} className="mr-1" /> View Report <ArrowRight size={12} className="ml-1" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
